import { ipaCombiners, ipaNonSyllabic, ipaToFeatures, IpaType } from "../phonemes/ipa";
import { segmentPhonemes } from "../phonemes/segmentPhonemes";

/** when multiple symbols do the same thing, pick one */
export function normIpa(ipa: IpaType): IpaType {
  const ipa2 = normDiacritics(ipa);
  const ipa3 = normCombos(ipa2);
  const ipa4 = normPunctuation(ipa3);
  return ipa4;
}

/** pick a single form for redundant diacritics */
function normDiacritics(ipa: IpaType): IpaType {
  let s = ipa.replace(/\u02D2|\u0357/g, '\u0339');  // more-rounded
  s = s.replace(/\u02D3|\u0351/g, '\u031C');  // less-rounded
  s = s.replace(/\u02D4/g, '\u031D'); // raised
  s = s.replace(/\u02D5/g, '\u031E'); // lowered
  s = s.replace(/\u02D6/g, '\u031F'); // advanced
  s = s.replace(/\u02D7/g, '\u0320'); // retracted
  s = s.replace(/\u0311/g, '\u032F'); // non-syllabic
  s = s.replace(/\u030D/g, '\u0329'); // syllabic
  s = s.replace(/\uAB6A/g, '\u0318'); // advanced-tongue-root
  s = s.replace(/\uAB6B/g, '\u0319'); // retracted-tongue-root
  return s;
}

/** convert combination characters to a normalized form, e.g., for diphthongs & affricates */
function normCombos(ipa: IpaType): IpaType {
  // use the default between-char combiner, e.g., /d͜ʒ/ > /d͡ʒ/
  const fixCombiners = ipa.replace(/\u035C|\u200D/g, ipaCombiners[0]);
  // use the default non-syllabic diacritic, e.g., /ɛɪ̑/ > /ɛɪ̯/
  const fixNonSyllabic = fixCombiners.replace(/\u0311/g, ipaNonSyllabic[0]);
  // treat combined vowels as a diphthong, e.g., /ɛ͡ɪ/ > /ɛɪ̯/
  const fixDiphthongs = combinedVowelsToDiphthong(fixNonSyllabic);
  return fixDiphthongs;
}

function combinedVowelsToDiphthong(ipa: IpaType): IpaType {
  if (!ipa.includes(ipaCombiners[0])) {
    return ipa;
  }

  // break into segments & turn into vowel-combiner-vowel segments into vowel-vowel(non-syllabic)
  let newIpa = '';
  const segments = segmentPhonemes(ipa);
  for (let i = 0; i < segments.phonemes.length; i++) {
    const segment = segments.phonemes[i].phoneme;
    let newSegment = segment;
    const where = segment.indexOf(ipaCombiners[0]);
    if (where !== -1) {
      // has a combiner
      const first = segment.substring(0, where);
      const last = segment.substring(where + 1);
      const fFeatures = ipaToFeatures(first);
      const lFeatures = ipaToFeatures(last);
      if (fFeatures?.includes('vowel') && lFeatures?.includes('vowel')) {
        // it combined two vowels, so switch style
        if (last[last.length - 1] === ipaNonSyllabic[0]) {
          newSegment = first + last;
        } else {
          newSegment = first + last + ipaNonSyllabic[0];
        }
      }
    }
    newIpa += newSegment;
  }

  return newIpa;
}

/** convert some punctuation to IPA prosody-break symbols */
function normPunctuation(s: IpaType): IpaType {
  s = s.replace(/, |,$/g, ' | ');
  s = s.replace(/: |:$/g, ' | ');
  s = s.replace(/; |;$/g, ' | ');
  s = s.replace(/\. |\.$/g, ' ‖ ');
  s = s.replace(/\? |\?$/g, ' ? ');
  s = s.replace(/! |!$/g, ' ! ');
  return s;
}
